import { mergeStyleSets, getTheme } from '@fluentui/react';
import type { ITheme } from '@fluentui/react';

const theme: ITheme = getTheme();

export const attestationPanelTextStyles = {
  stepTitle: { root: { fontWeight: 700 as const, fontSize: 16 } },
  stepSubtitle: { root: { fontSize: 13, color: '#605e5c' } },
  cardEmoji: { root: { fontSize: 18 } },
  cardTitle: { root: { fontWeight: 700 as const, fontSize: 15 } },
  modalHeaderTitle: { root: { fontWeight: 700 as const, fontSize: 20 } },
  instructions: { root: { fontSize: 13, color: '#323130', lineHeight: '20px' } },
  employeeDetail: { root: { fontSize: 13 } },
  successIcon: { root: { fontSize: 36, color: '#107c10' } },
  successSubtitle: { root: { fontSize: 13, color: '#605e5c', marginTop: 4 } },
  confirmDialogText: { root: { fontWeight: 700 as const, fontSize: 14 } },
  buttonHintText: { root: { fontSize: 11, color: '#6b7280', fontStyle: 'italic', marginLeft: 10, display: 'inline' } },
  closeButton: { root: { color: '#605e5c' } },
};

export const attestationPanelButtonStyles = {
  attestPrimary: {
    root: { borderRadius: 20, padding: '0 20px', fontWeight: 600 as const, fontSize: 13, minHeight: 32, background: '#0d9488', borderColor: '#0d9488' },
    rootHovered: { background: '#0f766e', borderColor: '#0f766e' },
  },
  sendReminder: {
    root: { borderRadius: 20, background: '#fff', borderColor: '#0d9488', color: '#0d9488', fontWeight: 600 as const, fontSize: 13 },
    rootHovered: { background: '#f0fdfa', borderColor: '#0d9488', color: '#0d9488' },
  },
  pauseCase: {
    root: { borderRadius: 20, background: 'linear-gradient(135deg, #d97706, #b45309)', color: '#fff', border: 'none', fontWeight: 600 as const, fontSize: 13 },
    rootHovered: { background: 'linear-gradient(135deg, #b45309, #92400e)', color: '#fff' },
  },
  resumeCase: {
    root: { borderRadius: 20, background: 'linear-gradient(135deg, #0d9488, #0f766e)', color: '#fff', border: 'none', fontWeight: 600 as const, fontSize: 13 },
    rootHovered: { background: 'linear-gradient(135deg, #0f766e, #115e59)', color: '#fff' },
  },
  cancelCase: {
    root: { borderRadius: 20, background: 'linear-gradient(135deg, #dc2626, #b91c1c)', color: '#fff', border: 'none', fontWeight: 600 as const, fontSize: 13 },
    rootHovered: { background: 'linear-gradient(135deg, #b91c1c, #991b1b)', color: '#fff' },
  },
  bypassManager: {
    root: { borderRadius: 20, background: 'transparent', color: '#4b5563', borderColor: '#d1d5db', fontWeight: 600 as const, fontSize: 13 },
    rootHovered: { background: '#f3f4f6', color: '#4b5563' },
  },
};

export const attestationPanelStyles = mergeStyleSets({
  container: {
    padding: '20px 0',
    width: '100%',
  },
  summaryCard: {
    background: theme.palette.white,
    border: `1px solid ${theme.palette.neutralLight}`,
    borderRadius: 8,
    padding: '20px',
    width: '100%',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
  },
  summaryCardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    marginBottom: 12,
  },
  summaryCardTitle: {
    fontSize: 15,
    fontWeight: 600 as const,
    color: '#111827',
    display: 'flex',
    alignItems: 'center',
  },
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 11,
    fontWeight: 600,
    padding: '3px 10px',
    borderRadius: 12,
    background: '#ede9fe',
    color: '#7c3aed',
  },
  // Use this only where the badge must be pushed to the right edge in a flex row.
  statusBadgeRight: {
    marginLeft: 'auto',
  },
  cardDivider: {
    borderTop: `1px solid ${theme.palette.neutralLight}`,
    paddingTop: 14,
    marginTop: 14,
  },
  formCard: {
    background: theme.palette.white,
    border: `1px solid ${theme.palette.neutralLight}`,
    borderRadius: 8,
    overflow: 'hidden',
  },
  formHeader: {
    background: theme.palette.themePrimary,
    padding: '16px 24px',
  },
  formTitle: {
    fontWeight: 700,
    fontSize: 16,
    color: '#ffffff',
  },
  formSubtitle: {
    fontSize: 12,
    color: 'rgba(255,255,255,0.85)',
    marginTop: 2,
  },
  employeeBar: {
    background: theme.palette.neutralLighterAlt,
    padding: '10px 16px',
    borderBottom: `1px solid ${theme.palette.neutralLight}`,
  },
  checkboxArea: {
    background: theme.palette.themeLighterAlt,
    border: `1px solid ${theme.palette.themeLighter}`,
    borderRadius: 6,
    padding: 12,
  },
  successContainer: {
    padding: 32,
    background: '#dff6dd',
    borderRadius: 8,
    textAlign: 'center' as const,
    border: '1px solid #a7e3a5',
  },
  successTitle: {
    fontWeight: 700,
    fontSize: 18,
    color: '#107c10',
    marginTop: 10,
  },
  successDetail: {
    fontSize: 13,
    color: theme.palette.neutralPrimary,
    marginTop: 8,
  },
  modalContainer: {
    width: '90%',
    maxWidth: 680,
    maxHeight: '85vh',
    borderRadius: 8,
    overflow: 'hidden',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 24px',
    borderBottom: `1px solid ${theme.palette.neutralLight}`,
  },
  modalBody: {
    padding: '16px 24px',
    overflowY: 'auto' as const,
    maxHeight: 'calc(85vh - 130px)',
  },
  modalFooter: {
    display: 'flex',
    gap: 8,
    padding: '12px 24px',
    borderTop: `1px solid ${theme.palette.neutralLight}`,
    background: theme.palette.neutralLighterAlt,
  },
});
